import { X } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { useBreakpoint } from '@/hooks/use-breakpoint';
import { useIsMobile } from '@/hooks/use-mobile';
import { useHelpPanel } from './help-panel-context';
import { helpContentRegistry } from './help-content-registry';
import { useLayoutEffect, useRef, type ComponentType } from 'react';

/** Tailwind default `lg`; sheet for viewports below this (md, sm, and base). */
const HELP_SHEET_MAX_BREAKPOINT_PX = 1400;

function HelpPanelBody({
  title,
  Content,
  closeLabel,
  onClose,
}: {
  title: string;
  Content: ComponentType;
  closeLabel: string;
  onClose: () => void;
}) {
  return (
    <>
      <div className="relative flex items-start justify-between gap-3 border-b px-4 py-4">
        <h2 className="pr-10 text-lg font-semibold leading-tight">{title}</h2>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-2 top-2 shrink-0"
          onClick={onClose}
          aria-label={closeLabel}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <ScrollArea className="min-h-0 flex-1 px-4 py-4">
        <div className="[&_p]:mb-3 [&_p:last-child]:mb-0 [&_code]:rounded-sm [&_code]:border [&_code]:border-border [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[0.8em] [&_hr]:my-4 [&_hr]:border-muted-foreground/30 prose prose-invert">
          <Content />
        </div>
      </ScrollArea>
    </>
  );
}

export function HelpPanel() {
  const { LL, locale } = useI18nContext();
  const isLgUp = useBreakpoint(HELP_SHEET_MAX_BREAKPOINT_PX);
  const isMobile = useIsMobile();
  const { activeHelpKey, closeHelp } = useHelpPanel();

  const entry = activeHelpKey ? helpContentRegistry[activeHelpKey] : undefined;
  const open = Boolean(entry);
  const title = entry ? entry.getTitle(LL, locale) : '';
  const Content = entry?.getContent(locale);
  const closeLabel = LL.metricsPage.help.closePanel();

  // The help icon that opened the bottom sheet gets the focus back on close.
  // A layout effect runs before the sheet moves the focus inside itself.
  const triggerRef = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (open && document.activeElement instanceof HTMLElement) {
      triggerRef.current = document.activeElement;
    }
  }, [open]);

  // SALES-04 AC 6: below 768px the help opens as a bottom sheet.
  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            closeHelp();
          }
        }}
      >
        <DrawerContent
          id="analytics-help-panel"
          returnFocusRef={triggerRef}
          aria-describedby={undefined}
        >
          {open && Content ? (
            <>
              <DrawerTitle className="sr-only">{title}</DrawerTitle>
              <HelpPanelBody
                title={title}
                Content={Content}
                closeLabel={closeLabel}
                onClose={closeHelp}
              />
            </>
          ) : (
            <DrawerTitle className="sr-only">
              {LL.metricsPage.help.ariaLabel()}
            </DrawerTitle>
          )}
        </DrawerContent>
      </Drawer>
    );
  }

  if (!isLgUp) {
    return (
      <Sheet
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            closeHelp();
          }
        }}
      >
        <SheetContent
          id="analytics-help-panel"
          side="right"
          overlayClassName="z-[70]"
          className={cn(
            'z-[70] flex h-full w-full max-w-[min(100vw,400px)] flex-col gap-0 border-l p-0 sm:max-w-[400px]',
            '[&>button]:hidden'
          )}
          aria-describedby={undefined}
        >
          {open && Content ? (
            <>
              <SheetTitle className="sr-only">{title}</SheetTitle>
              <HelpPanelBody
                title={title}
                Content={Content}
                closeLabel={closeLabel}
                onClose={closeHelp}
              />
            </>
          ) : (
            <SheetTitle className="sr-only">
              {LL.metricsPage.help.ariaLabel()}
            </SheetTitle>
          )}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside
      id="analytics-help-panel"
      aria-hidden={!open}
      className={cn(
        'flex min-h-0 shrink-0 flex-col border-l bg-background transition-[width,opacity] duration-300 ease-in-out',
        open
          ? 'w-[min(100vw,400px)] opacity-100'
          : 'w-0 overflow-hidden border-l-0 opacity-0'
      )}
    >
      {open && Content ? (
        <div className="flex h-full min-h-0 min-w-[min(100vw,400px)] flex-1 flex-col">
          <HelpPanelBody
            title={title}
            Content={Content}
            closeLabel={closeLabel}
            onClose={closeHelp}
          />
        </div>
      ) : null}
    </aside>
  );
}
